/**
 * useReportsDataSource — wires wig-react-library's report components
 * (`/reports`) to this app: auth from AppContext, calls through
 * `reportsService`, errors through `apiErrorText`. Values use the library's
 * default formatter (`formatReportValue`, the same named number formats).
 * Pick-list / lookup labels come with the catalog (`options` per field), so
 * no lookup tables are fetched here.
 *
 * Returns `{ dataSource, call }`:
 *   - `dataSource` goes to `ReportsDataSourceProvider`.
 *   - `call(fn, args)` runs any `reportsService` method with a fresh token and
 *     the user's OID merged in — the pages use it for list / get / save.
 *
 * `runQuery` and `call` keep a stable identity across token refreshes (they
 * read auth through a ref), so a widget does not re-run its query just
 * because the token was renewed.
 *
 * Drill-down: `buildDrill` maps a clicked slice to the deals-list filter
 * (utils/reportDrill — the app's own filter vocabulary; null where the
 * mapping is not exact, which makes the slice unclickable), the library asks
 * "Open these deals?" (`drillNoun`), and `onDrill` opens the deals
 * list in a NEW tab — leaving the report where it is — filtered to the slice.
 * The filter travels through localStorage under a one-time key
 * (utils/drillHandoff) as `/?drill=<id>`; this tab's own remembered deals
 * filter (sessionStorage) is not touched. If storage is unavailable the
 * deals list still opens, unfiltered, and the user is told why.
 * `buildRowDrill` maps a "detail rows" grid row to `deal_key eq <key>` when
 * the row carries the deal key (opened as the deal page, /deals/<key>), else
 * to `id eq <deal id>`, which takes the deals-list route above.
 *
 * Report sources: the six SQL-source calls (`listSources` … `previewSource`)
 * are on the same data source, for WigReportSources (`/reports/sources`).
 */
import { useCallback, useContext, useMemo, useRef } from "react";

import { AppContext } from "../AppContextProvider";
import reportsService from "../services/reportsService";
import { apiErrorText } from "../utils/apiError";
import { buildDrillFilter } from "../utils/reportDrill";
import { message } from "antd";
import { DRILL_PARAM, stashDrill } from "../utils/drillHandoff";
import spatialService from "../services/spatialService";
import { loadMapboxToken } from "./useMapboxToken";

const EMPTY_FC = { type: "FeatureCollection", features: [] };

// Spatial layers are static reference data: one fetch per (kind, state,
// county) per tab, shared by every map widget.
const geometryCache = new Map();

// The spatial API answers with a bare FeatureCollection or the de-hub
// envelope; a 404 or anything else is "no geometry here" (only OK / TX have
// counties and sections today), never an error that blanks the widget.
const pickFC = (resp) => {
  const body = resp?.data;
  if (body?.type === "FeatureCollection") return body;
  if (body?.data?.type === "FeatureCollection") return body.data;
  if (Array.isArray(body?.data?.features)) {
    return { type: "FeatureCollection", features: body.data.features };
  }
  return EMPTY_FC;
};

/**
 * A clicked "detail rows" grid row (rows-mode query) -> the one deal it
 * belongs to, as a deals-list filter on the deal's id. Goes through the same
 * confirm dialog and `onDrill` as a slice drill.
 */
export const buildRowDrill = ({ dealId, row }) => {
  // deal_key is unique (ux_deals_deal_key), so a row that carries it can open
  // the deal page itself; otherwise fall back to the deals list on the id.
  const dealKey = row?.deal_key ?? row?.deals__deal_key;
  if (dealKey != null && dealKey !== "") {
    return {
      combinator: "and",
      rules: [{ field: "deal_key", operator: "eq", value: String(dealKey) }],
    };
  }
  return {
    combinator: "and",
    rules: [{ field: "id", operator: "eq", value: dealId }],
  };
};

/** The deal key when `filter` is exactly one `deal_key eq <key>` rule. */
export const singleDealKey = (filter) => {
  const rules = filter?.rules;
  if (!Array.isArray(rules) || rules.length !== 1) return null;
  const [r] = rules;
  return r?.field === "deal_key" && r?.operator === "eq" && r?.value ? String(r.value) : null;
};

export default function useReportsDataSource() {
  const { token, getToken, isTokenExpired, user, theme } = useContext(AppContext);

  const authRef = useRef();
  authRef.current = { token, getToken, isTokenExpired, user };

  const call = useCallback(async (fn, args = {}) => {
    const a = authRef.current;
    const userOid = a.user?.idTokenClaims?.oid || a.user?.localAccountId;
    if (!userOid) throw new Error("OID not found in user claims.");
    let tk = a.token;
    if (!tk || a.isTokenExpired(tk)) tk = await a.getToken();
    return fn({ token: tk, userOid, ...args });
  }, []);

  const runQuery = useCallback(
    async (query) => (await call(reportsService.runQuery, { query }))?.data?.data,
    [call]
  );

  // Map widgets: geometry by kind ("states" | "counties" | "sections").
  const getGeometry = useCallback(
    async (kind, { state, county } = {}) => {
      const key = `${kind}|${state || ""}|${county || ""}`;
      if (geometryCache.has(key)) return geometryCache.get(key);
      const load = ({ token: tk, userOid }) => {
        if (kind === "states") return spatialService.getStates(tk, userOid);
        if (kind === "counties") return spatialService.getCounties(tk, userOid, state);
        return spatialService.getSections(tk, userOid, { state, county });
      };
      let fc = EMPTY_FC;
      try {
        fc = pickFC(await call(load));
      } catch (err) {
        if (err?.response?.status !== 404) throw err;
      }
      geometryCache.set(key, fc);
      return fc;
    },
    [call]
  );

  // The Mapbox token from Key Vault (shared cache with useMapboxToken), with
  // the same local-dev fallback the deals map uses.
  const getMapboxToken = useCallback(async () => {
    try {
      const t = await call(({ token: tk }) => loadMapboxToken(tk));
      if (t) return t;
    } catch (err) {
      if (!import.meta.env.VITE_MAPBOX_TOKEN) throw err;
    }
    if (import.meta.env.VITE_MAPBOX_TOKEN) return import.meta.env.VITE_MAPBOX_TOKEN;
    throw new Error("No Mapbox token available from Key Vault or VITE_MAPBOX_TOKEN.");
  }, [call]);

  const onDrill = useCallback(
    (filter) => {
      // A detail row with its deal key opens that deal's page directly.
      const dealKey = singleDealKey(filter);
      if (dealKey) {
        try {
          window.open(`/deals/${encodeURIComponent(dealKey)}`, "_blank", "noopener");
        } catch {
          // A blocked or failed popup is not worth a crash.
        }
        return;
      }
      const id = stashDrill(filter);
      const url = id ? `/?${DRILL_PARAM}=${encodeURIComponent(id)}` : "/";
      try {
        window.open(url, "_blank", "noopener");
      } catch {
        // A blocked or failed popup is not worth a crash.
      }
      if (!id) {
        message.warning(
          "Couldn't pass the filter to the new tab (browser storage is unavailable), so the deals list opened unfiltered."
        );
      }
    },
    []
  );

  // SQL report sources (WigReportSources on /reports/sources): each resolves
  // with the unwrapped de-hub `data` and rejects with the raw axios error,
  // whose `response.status` / `response.data.detail` the library reads.
  const sources = useMemo(() => {
    const unwrap = async (fn, args) => (await call(fn, args))?.data?.data;
    return {
      listSources: () => unwrap(reportsService.listSources),
      getSource: (id) => unwrap(reportsService.getSource, { id }),
      createSource: (body) => unwrap(reportsService.createSource, { body }),
      updateSource: (id, body) => unwrap(reportsService.updateSource, { id, body }),
      deleteSource: (id) => unwrap(reportsService.deleteSource, { id }),
      previewSource: (sql) => unwrap(reportsService.previewSource, { sql }),
    };
  }, [call]);

  const dataSource = useMemo(
    () => ({
      runQuery,
      errorText: (err) => apiErrorText(err),
      darkMode: theme === "dark",
      buildDrill: buildDrillFilter,
      buildRowDrill,
      drillNoun: "deals",
      onDrill,
      getGeometry,
      getMapboxToken,
      ...sources,
    }),
    [runQuery, theme, onDrill, getGeometry, getMapboxToken, sources]
  );

  return { dataSource, call };
}
